import React, { useContext, useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { useDispatch } from "react-redux";
import { AuthContext } from "../../../context/authContext";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectOption } from "../../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import DataTable from "../../../components/shared/DataTable";
import RefreshButton from "../../../components/shared/RefreshButton";
import StatusBadge from "../../../components/shared/StatusBadge";
import {
  createApiKey,
  getAllApiKeys,
  updateApiKeyStatus,
} from "../../../reducers/api_key.slice";
import { convertDateToCT } from "../../../utils/util";

const EXPIRY_OPTIONS = [
  { value: "7_DAYS", label: "7 days" },
  { value: "1_MONTH", label: "1 month" },
  { value: "1_YEAR", label: "1 year" },
  { value: "NO_EXPIRY", label: "No expiry" },
];

const emptyForm = { name: "", description: "", expiry_type: "1_YEAR" };

const ApiKeysTab = () => {
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);

  const [apiKeys, setApiKeys] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [createdKeyRaw, setCreatedKeyRaw] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  const fetchApiKeys = () => {
    setShowLoader(true);
    dispatch(getAllApiKeys()).then((res) => {
      setShowLoader(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      setApiKeys(res?.payload || []);
    });
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setForm(emptyForm);
    setCreatedKeyRaw(null);
    setIsCopied(false);
  };

  const handleCreateKey = () => {
    if (!form.name.trim()) {
      showToast("Please enter a name for the API key", "error");
      return;
    }
    setShowLoader(true);
    dispatch(
      createApiKey({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        expiry_type: form.expiry_type,
      })
    ).then((res) => {
      setShowLoader(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      setCreatedKeyRaw(res?.payload?.api_key);
      showToast("API key created successfully", "success");
      fetchApiKeys();
    });
  };

  const handleCopyKey = async () => {
    if (!createdKeyRaw) return;
    try {
      await navigator.clipboard.writeText(createdKeyRaw);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch {
      showToast("Failed to copy API key", "error");
    }
  };

  const handleToggleStatus = (row) => {
    const newStatus = row?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setShowLoader(true);
    dispatch(updateApiKeyStatus({ id: row?.id, status: newStatus })).then((res) => {
      setShowLoader(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      showToast(
        `API key "${row?.name}" ${newStatus === "ACTIVE" ? "activated" : "deactivated"}`,
        "success"
      );
      fetchApiKeys();
    });
  };

  const columns = [
    { key: "name", label: "Name", width: 140 },
    {
      key: "key_prefix",
      label: "Key",
      width: 160,
      render: (row) => (
        <span style={{ fontFamily: "ui-monospace, SF Mono, Menlo, monospace", fontSize: 13 }}>
          {row?.key_prefix}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: 100,
      align: "center",
      render: (row) => <StatusBadge status={row?.status} />,
    },
    { key: "expiry_type", label: "Expiry", width: 100 },
    {
      key: "last_used_at",
      label: "Last Used",
      width: 180,
      render: (row) => (
        <span style={{ fontSize: 13 }}>
          {row?.last_used_at
            ? convertDateToCT(row?.last_used_at, "MMMM D, YYYY hh:mm:ss A")
            : "Never"}
        </span>
      ),
    },
    { key: "usage_count", label: "Usage Count", width: 110, align: "right" },
    {
      key: "created_at",
      label: "Created At",
      width: 180,
      render: (row) => (
        <span style={{ fontSize: 13 }}>
          {convertDateToCT(row?.created_at, "MMMM D, YYYY hh:mm:ss A")}
        </span>
      ),
    },
    {
      key: "action",
      label: "Action",
      width: 110,
      align: "center",
      render: (row) => (
        <Button
          size="sm"
          variant={row?.status === "ACTIVE" ? "destructive" : "default"}
          style={
            row?.status !== "ACTIVE"
              ? { background: "#4caf50", color: "#fff", fontWeight: 400, fontSize: "0.85rem" }
              : { fontWeight: 400, fontSize: "0.85rem" }
          }
          onClick={() => handleToggleStatus(row)}
        >
          {row?.status === "ACTIVE" ? "Deactivate" : "Activate"}
        </Button>
      ),
    },
  ];

  const paginatedRows = apiKeys.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#fff", margin: 0 }}>API Keys</h2>
          <p style={{ color: "#8b9296", fontSize: 13, margin: "4px 0 0" }}>
            Generate and manage read-only API keys for external integrations.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginLeft: "auto" }}>
          <RefreshButton onRefresh={fetchApiKeys} spinning={showLoader} />
          <Button onClick={() => setIsCreateDialogOpen(true)}>Create API Key</Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={paginatedRows}
        totalRows={apiKeys.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(n) => setCurrentPage(n)}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setCurrentPage(0); }}
        loading={showLoader}
        minWidth={980}
      />

      <Dialog open={isCreateDialogOpen} onOpenChange={(open) => !open && handleCloseCreateDialog()}>
        <DialogContent style={{ maxWidth: 520 }}>
          <DialogHeader>
            <DialogTitle>{createdKeyRaw ? "API Key Created" : "Create API Key"}</DialogTitle>
            <DialogDescription>
              {createdKeyRaw
                ? "Store this key somewhere safe — you won't be able to view it again."
                : "Generate a read-only key for external integrations."}
            </DialogDescription>
          </DialogHeader>

          {createdKeyRaw ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <p style={{ fontSize: 13, color: "#e8b84b", margin: 0 }}>
                Copy this key now — it will not be shown again.
              </p>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Input
                  value={createdKeyRaw}
                  readOnly
                  style={{ fontFamily: "ui-monospace, SF Mono, Menlo, monospace", fontSize: 12 }}
                />
                <Button
                  variant="outline"
                  size="icon"
                  title={isCopied ? "Copied" : "Copy"}
                  onClick={handleCopyKey}
                >
                  {isCopied ? <Check size={15} style={{ color: "#4caf50" }} /> : <Copy size={15} />}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <Label htmlFor="api-key-name">Name *</Label>
                <Input
                  id="api-key-name"
                  placeholder="e.g. Inventory Sync Key"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="api-key-desc">Description</Label>
                <Textarea
                  id="api-key-desc"
                  placeholder="Optional description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="api-key-expiry">Expiration</Label>
                <Select
                  id="api-key-expiry"
                  value={form.expiry_type}
                  onChange={(e) => setForm({ ...form, expiry_type: e.target.value })}
                >
                  {EXPIRY_OPTIONS.map((option) => (
                    <SelectOption key={option.value} value={option.value}>
                      {option.label}
                    </SelectOption>
                  ))}
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            {createdKeyRaw ? (
              <Button onClick={handleCloseCreateDialog}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleCloseCreateDialog}>Cancel</Button>
                <Button disabled={showLoader} onClick={handleCreateKey}>
                  {showLoader ? "Creating..." : "Create"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ApiKeysTab;
